import { fullscreenIsAvailable, isFullscreen } from "common/utilities/fullscreen";
import { getCollectionAction, getCollectionMenuItemId } from "domains/nav/constants/menuItemPrefixes";
import type { SubnavMenuItemInput, SubnavMenuItemInputWithoutChildren } from "domains/nav/types/hostCommand";
import { SubnavMenuItemIcon } from "domains/nav/types/hostCommand";
import { SubnavAction } from "domains/nav/types/subnavAction";
import { SubnavMenuItemId } from "domains/nav/types/subnavMenuItemId";
import type { Theme } from "domains/theme/types";

type ExtractTerms<T extends readonly string[]> = Record<T[number], string>;

type CreateTerms = ExtractTerms<typeof createTermNames>;
type DiscoverTerms = ExtractTerms<typeof discoverTermNames>;
type HomeTerms = ExtractTerms<typeof homeTermNames>;
type MonitorTerms = ExtractTerms<typeof monitorTermNames>;
type MoreTerms = ExtractTerms<typeof moreTermNames>;

export const createTermNames = ["app.create.title"] as const;
export const discoverTermNames = ["app.discover.title", "app.topic.all_topics", "app.topic.custom_maps"] as const;
export const homeTermNames = ["app.home"] as const;
export const monitorTermNames = ["app.monitor.title"] as const;

export const moreTermNames = [
  "app.bookmarks.title",
  "app.fullscreen_exit.title",
  "app.fullscreen.title",
  "app.monitor.see_more_topics",
  "app.navigation.about_link",
  "app.navigation.whats_new",
  "app.profile.menu.coachmarks_settings",
  "app.profile.menu.language",
  "app.resources.list.title",
  "app.settings.feedback",
  "app.settings.terms_of_use",
  "app.settings.theme.title",
  "app.widget.embed_maps_in_your_site",
] as const;

export const buildCreate = (terms: CreateTerms) => ({
  action: SubnavAction.CREATE,
  id: SubnavMenuItemId.CREATE,
  title: terms["app.create.title"],
});

export const buildDiscover = (
  terms: DiscoverTerms,
  listedCollectionDetails: { id: string; name: string }[],
  customTopicCount: number,
) => ({
  children: [
    {
      action: SubnavAction.ALL_TOPICS,
      icon: SubnavMenuItemIcon.CHEVRON_RIGHT,
      id: SubnavMenuItemId.ALL_TOPICS,
      title: terms["app.topic.all_topics"],
    },
    ...(listedCollectionDetails.length > 0
      ? [
          buildDivider(1),
          ...listedCollectionDetails.map(({ id, name }) => ({
            action: getCollectionAction(id),
            icon: SubnavMenuItemIcon.CHEVRON_RIGHT,
            id: getCollectionMenuItemId(id),
            title: name,
          })),
        ]
      : []),
    ...(customTopicCount > 0
      ? [
          buildDivider(2),
          {
            action: SubnavAction.CUSTOM_MAPS,
            icon: SubnavMenuItemIcon.CHEVRON_RIGHT,
            id: SubnavMenuItemId.CUSTOM_MAPS,
            title: terms["app.topic.custom_maps"],
          },
        ]
      : []),
  ],
  icon: SubnavMenuItemIcon.CHEVRON_DOWN,
  id: SubnavMenuItemId.DISCOVER,
  title: terms["app.discover.title"],
});

const buildDivider = (ordinal: number): SubnavMenuItemInputWithoutChildren => ({
  divider: true,
  id: `DIVIDER_${ordinal}`,
});

export const buildHome = (terms: HomeTerms): SubnavMenuItemInput => ({
  action: SubnavAction.HOME,
  id: SubnavMenuItemId.HOME,
  title: terms["app.home"],
});

export const buildMonitor = (terms: MonitorTerms) => ({
  action: SubnavAction.MONITOR,
  id: SubnavMenuItemId.MONITOR,
  title: terms["app.monitor.title"],
});

export const buildMore = (
  terms: MoreTerms,
  isAuthenticated: boolean,
  showWhatsNew: boolean,
  theme: Theme,
  canDisplayBookmarks: boolean,
) => ({
  children: [
    ...(fullscreenIsAvailable()
      ? [
          {
            action: SubnavAction.FULLSCREEN,
            icon: SubnavMenuItemIcon.FULLSCREEN,
            id: SubnavMenuItemId.FULLSCREEN,
            title: isFullscreen() ? terms["app.fullscreen_exit.title"] : terms["app.fullscreen.title"],
          },
        ]
      : []),
    { action: SubnavAction.LANGUAGE, id: SubnavMenuItemId.LANGUAGE, title: terms["app.profile.menu.language"] },
    ...(isAuthenticated
      ? [
          {
            action: SubnavAction.COACHMARK_SETTINGS,
            id: SubnavMenuItemId.COACHMARK_SETTINGS,
            title: terms["app.profile.menu.coachmarks_settings"],
          },
        ]
      : []),
    ...(showWhatsNew
      ? [{ action: SubnavAction.WHATS_NEW, id: SubnavMenuItemId.WHATS_NEW, title: terms["app.navigation.whats_new"] }]
      : []),
    {
      action: SubnavAction.EMBED_MAPS_IN_YOUR_SITE,
      id: SubnavMenuItemId.EMBED_MAPS_IN_YOUR_SITE,
      title: terms["app.widget.embed_maps_in_your_site"],
    },
    { action: SubnavAction.ABOUT, id: SubnavMenuItemId.ABOUT, title: terms["app.navigation.about_link"] },
    { action: SubnavAction.TERMS, id: SubnavMenuItemId.TERMS, title: terms["app.settings.terms_of_use"] },
    { action: SubnavAction.FEEDBACK, id: SubnavMenuItemId.FEEDBACK, title: terms["app.settings.feedback"] },
    {
      action: SubnavAction.THEME,
      icon: theme === "dark" ? SubnavMenuItemIcon.DARK : SubnavMenuItemIcon.LIGHT,
      id: SubnavMenuItemId.THEME,
      title: terms["app.settings.theme.title"],
    },
    { action: SubnavAction.RESOURCES, id: SubnavMenuItemId.RESOURCES, title: terms["app.resources.list.title"] },
    ...(canDisplayBookmarks
      ? [{ action: SubnavAction.BOOKMARKS, id: SubnavMenuItemId.BOOKMARKS, title: terms["app.bookmarks.title"] }]
      : []),
  ],
  icon: SubnavMenuItemIcon.CHEVRON_DOWN,
  id: SubnavMenuItemId.MORE,
  title: terms["app.monitor.see_more_topics"],
});
